import { useState } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEditorWebsocket } from "@hooks";
import {
  useEstimateEditorStore,
  useEstimateEditorSettings,
  ElementChatPopup,
  VisibilityButton, useEstimateStructure
} from "@tools";
import { WORK_TYPE_SETTINGS } from "@tools/EstimateEditor/utils";
import { Alert, Dialog, Tag, TooltipIcon } from "@components";
import { Grid } from "@material-ui/core";
import { AutomationSettings } from "./AutomationSettings";
import { DisplaySettings } from "./DisplaySettings/DisplaySettings";
import { RoundingSettings } from "./RoundingSettings";
import { Percent, Settings, Trash } from "@assets";
import { Cached, Visibility } from "@material-ui/icons";
import classnames from "classnames";

export const SummaryRowActions = observer(({
  workType,
  opened
}) => {
  const { t } = useTranslation();
  const { visibilityMode } = useEstimateEditorStore();
  const { showChatThread, allowEdition } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const socket = useEditorWebsocket();

  const [ removeAlertOpened, openRemoveAlert ] = useState(false);
  const [ open, setOpen ] = useState(false);
  const [ expandedAccordion, setExpandedAccordion ] = useState(null);
  const [ expandedAccordionOrigin, setExpandedAccordionOrigin ] = useState(null);
  const [ advancedSettingsExpanded, setAdvancedSettingsExpanded ] = useState(false);
  
  const { name, isEmpty, isHidden, treePath } = workType;
  
  const allVisible = structure.getWorkTypeVisibility(workType.id);
  const commentPath = treePath.join("/");
  
  const commitRemoval = async () => {
    workType.removeSelf();
    openRemoveAlert(false);
    socket?.requestCommentPathRemove(commentPath);
    socket?.requestCommentPathRemove("t"+commentPath);
  };
  
  const handleClose = () => {
    setOpen(false);
    setExpandedAccordion(null);
    setExpandedAccordionOrigin(null);
    setAdvancedSettingsExpanded(false);
  }
  
  const handleOpen = (defaultExpandedAccordion) => () => {
    if(defaultExpandedAccordion) {
      setExpandedAccordion(defaultExpandedAccordion);
      setExpandedAccordionOrigin(defaultExpandedAccordion);
    }
    setOpen(true);
  };
  
  const handleAccordionToggle = (accordionId) => {
    setAdvancedSettingsExpanded(false);
    if(accordionId === expandedAccordionOrigin && accordionId === expandedAccordion) {
      handleClose();
    } else {
      setExpandedAccordionOrigin(null);
      setExpandedAccordion(expandedAccordion === accordionId ? null : accordionId);
    }
  };

  const toggleAdvancedSettings = () => setAdvancedSettingsExpanded((state) => !state);

  const handleRemoveAlert = () => openRemoveAlert(!removeAlertOpened);

  return (
    <>
      {
        allowEdition && !isEmpty &&
        <>
          <TooltipIcon
            icon={<Percent />}
            onClick={handleOpen(WORK_TYPE_SETTINGS.AUTOMATION)}
            color="primary"
            className={classnames(
              "button-wta-settings noDrag",
              workType.hasPercent ? "order-first" : "hoverIcon opq"
            )}
          >
            {t("views.editor.dialogs.work_type_settings.automation")}
          </TooltipIcon>
          {
            workType.hasRounding &&
            <TooltipIcon
              icon={<Cached />}
              onClick={handleOpen(WORK_TYPE_SETTINGS.ROUNDING)}
              color="primary"
              className={classnames("button-wta-settings noDrag order-first")}
            >
              {t("views.editor.dialogs.work_type_settings.rounding")}
            </TooltipIcon>
          }
          {
            !allVisible &&
            <TooltipIcon
              icon={<Visibility />}
              color="primary"
              onClick={handleOpen(WORK_TYPE_SETTINGS.DISPLAY)}
              className={classnames("button-wta-settings noDrag order-first")}
            >
              { t("views.editor.dialogs.work_type_settings.display.title") }
            </TooltipIcon>
          }
        </>
      }
      {
        visibilityMode &&
        <VisibilityButton
          hidden={isHidden}
          onChange={workType.setIsHidden}
          inRow
        />
      }
      {
        showChatThread &&
        <ElementChatPopup
          rowThread
          path={commentPath}
          taskName={name || t("common.unnamed")}
          hideBadge={opened}
        />
      }
      {
        allowEdition &&
        <>
          <TooltipIcon
            icon={<Settings />}
            onClick={handleOpen()}
            color="primary"
            className="button-wt-settings noDrag hoverIcon opq"
          >
            {t("views.editor.dialogs.work_type_settings.title")}
          </TooltipIcon>
          <TooltipIcon
            icon={<Trash color="error" />}
            onClick={isEmpty ? commitRemoval : handleRemoveAlert}
            className="button-remove noDrag hoverIcon opq"
          >
            {t("views.editor.remove_workType")}
          </TooltipIcon>
          <Alert
            isOpen={removeAlertOpened}
            title={t("alerts.editor.tag_remove")}
            onAccept={commitRemoval}
            onCancel={handleRemoveAlert}
            acceptText={t("common.remove")}
            cancelText={t("common.cancel")}
          />
          <Dialog
            id="settings-dialog"
            open={open}
            onClose={handleClose}
            title={<Grid container alignItems="center">
              <h2 className="w-max">{t("views.editor.dialogs.work_type_settings.title")}</h2>
              <Tag
                maxWidth={260}
                ellipsisOnOverflow
                text={workType.name || t("common.unnamed")}
                className="ml-2 mt-1-5"
              />
            </Grid>}
            width={
              advancedSettingsExpanded && [
                WORK_TYPE_SETTINGS.AUTOMATION,
                WORK_TYPE_SETTINGS.ROUNDING,
              ].includes(expandedAccordion)
                ? 920
                : 640
            }
            animateWidthChange
          >
            <AutomationSettings
              workType={workType}
              expandedAccordion={expandedAccordion}
              onToggle={handleAccordionToggle}
              advancedSettingsExpanded={advancedSettingsExpanded}
              onAdvancedSettingsToggle={toggleAdvancedSettings}
            />
            <RoundingSettings
              workType={workType}
              expandedAccordion={expandedAccordion}
              onToggle={handleAccordionToggle}
              advancedSettingsExpanded={advancedSettingsExpanded}
              onAdvancedSettingsToggle={toggleAdvancedSettings}
            />
            <DisplaySettings
              workType={workType}
              expandedAccordion={expandedAccordion}
              onToggle={handleAccordionToggle}
            />
          </Dialog>
        </>
      }
    </>
  );
});

SummaryRowActions.propTypes = {
  workType: object.isRequired,
  opened: bool,
};
